import { Injectable, OnDestroy } from "@angular/core";
import { Subscription } from "rxjs";

import { FieldGroupMetadata, FieldMetadata, FieldWidthEnum, ViewMetadata } from "src/cer/cer-data/cer-data-types";
import { UiCommand, UiCommandEvent } from "@ui/cer-ui-types";
import { CerPromptService } from "@ui/cer-ui-prompt.service";
import { ICerViewComponent } from "@form/cer-form-view";

import { WarehouseOutboundOrderClient, WarehouseOutboundOrderStatusEnum } from "../api";
import { CerAppDataNoteService } from "src/cer-app/cer-app-data-note/cer-app-data-note.service";
import { CerUiIcons } from "@ui/cer-ui-icons";
import { uiCmdSeparator } from "@ui/cer-ui-commands";
import { CerGridComponent } from "src/cer/cer-grid/cer-grid.component";
import { CerFormPanesSettings } from "@cer/cer-form/cer-form-panes/cer-form-panes.service";

export const uiCmdOrderSyncSelected: UiCommand = { id: 'orderSyncSelected', text: 'Opdater ordre status', tooltipText: 'Opdater status på denne forsendelse/ordre', iconCss: CerUiIcons.research, target: ".e-content", needSelection: true };
export const baseUrlTrackTrace = 'https://www.postnord.dk/varktojer/track-trace?shipmentId=';

export const orderPanes: CerFormPanesSettings = {
  main: { visible: 'show', size: '60%' },
  bottom: { visible: 'show', size: '40%', sizeSecondary: '60%' },
  bottomRight: { visible: 'show', size: '40%' },
  orientationPrimary: 'vertical'
};

export const orderWarehouseOutboundOrderPanes: CerFormPanesSettings = {
  top: { visible: 'show', size: '40%' },
  main: { visible: 'show', size: '20%', sizeSecondary: '50%' },
  right: { visible: 'show', size: '50%' },
  bottom: { visible: 'show', size: '40%', sizeSecondary: '50%' },
  bottomRight: { visible: 'show', size: '50%' },
  orientationPrimary: 'vertical'
};

const uiCmdWarehouseOutboundOrderRelease: UiCommand =
  { id: 'warehouseOutboundOrderRelease', text: 'Frigiv forsendelse', tooltipText: 'Frigiv forsendelse til lager', iconCss: CerUiIcons.changesAccept, target: ".e-content", needSelection: true };
const uiCmdWarehouseOutboundOrderCancel: UiCommand =
  { id: 'warehouseOutboundOrderCancel', text: 'Annuller forsendelse', tooltipText: 'Annuller forsendelse', iconCss: CerUiIcons.changesReject, target: ".e-content", needSelection: true };
const uiCmdWarehouseOutboundOrderComplete: UiCommand =
  { id: 'warehouseOutboundOrderComplete', text: 'Afslut forsendelse', tooltipText: 'Afslut forsendelse', iconCss: CerUiIcons.changesComplete, target: ".e-content", needSelection: true };
const uiCmdWarehouseOutboundOrderReorderCancelledLines: UiCommand =
  { id: 'warehouseOutboundOrderReorderCancelledLines', text: 'Genbestil annullerede', tooltipText: 'Genbestil annullerede linjer', iconCss: CerUiIcons.changesComplete, target: ".e-content", needSelection: true };
const uiCmdSalesOrderSync: UiCommand = { id: 'uiCmdSalesOrderSync', text: 'Hent status', tooltipText: 'Hent data omkring forsendelser', iconCss: CerUiIcons.research, target: ".e-content" };
const uiCmdOrderSync: UiCommand = { id: 'uiCmdOrderSync', text: 'Hent status salg og forsendelser', tooltipText: 'Hent data omkring salg og forsendelser', iconCss: CerUiIcons.research, target: ".e-content" };


@Injectable({ providedIn: 'root' })
export class WarehouseOutboundOrderService implements OnDestroy {

  private subscriptionManager: Subscription = new Subscription();
  private manage(s: Subscription) {
    this.subscriptionManager.add(s);
  }

  constructor(
    dataNoteService: CerAppDataNoteService,
    private outboundOrderClient: WarehouseOutboundOrderClient,
    private ui: CerPromptService) {

    var dataNoteTables: string[] = ['WarehouseOutboundOrder', 'SalesOrder'];
    this.dataNoteViewMetadata = dataNoteService.viewMetadataGet(dataNoteTables);
    this.dataNoteFieldMetadata = dataNoteService.fieldMetadataGet(dataNoteTables[0]);
  }

  ngOnDestroy(): void {
    this.subscriptionManager.unsubscribe();
  }

  public orderToolbarSimpleCommands: UiCommand[] = [uiCmdWarehouseOutboundOrderRelease];
  public orderToolbarCommands: UiCommand[] = [uiCmdSalesOrderSync, uiCmdSeparator, uiCmdWarehouseOutboundOrderRelease];
  public orderContextMenuCommands: UiCommand[] = [uiCmdOrderSyncSelected, uiCmdSeparator, uiCmdWarehouseOutboundOrderRelease, uiCmdWarehouseOutboundOrderCancel, uiCmdWarehouseOutboundOrderComplete,
    uiCmdSeparator, uiCmdWarehouseOutboundOrderReorderCancelledLines, uiCmdSeparator,
    uiCmdSeparator, uiCmdOrderSync, uiCmdSalesOrderSync];

  public orderViewMetadata: ViewMetadata = {
    name: 'outbound-order', dataApiName: 'WarehouseOutboundOrder', text: 'Forsendelser', textSingular: 'Forsendelse',
    primaryKey: 'id', parentPrimaryKey: 'num', parentForeignKey: 'salesOrder', titleFields: ['order', 'type', 'product'], allowEdit: true, allowCreate: true, allowDelete: true, baseFields: ['history']
  };

  public orderFieldMetadata: FieldMetadata[] = [
    { name: 'id', text: 'Id', required: true, tooltipText: 'Unik nøgle', allowEdit: false, visible: false, widthType: FieldWidthEnum.XS },
    { name: 'num', text: 'Ordrenr.', required: true, tooltipText: 'Ordrenummer', widthType: FieldWidthEnum.S, orderBy: 'Descending' },
    { name: 'warehouse', text: 'Lager', required: true, widthType: FieldWidthEnum.XXS, foreignKeyTableName: 'Warehouse', foreignKeyField: 'num', foreignKeyValue: 'num', foreignKeyColumns: ['num', 'name'], foreignKeyTitles: ['Lager', 'Navn'] },
    { name: 'type', text: 'Type', tooltipText: 'Forsendelsestype', widthType: FieldWidthEnum.S, foreignKeyTableName: 'WarehouseOutboundOrderType', foreignKeyField: 'num', foreignKeyValue: 'num' },
    { name: 'status', text: 'Status', tooltipText: 'Forsendelsestatus', widthType: FieldWidthEnum.S, foreignKeyTableName: 'WarehouseOutboundOrderStatus', foreignKeyField: 'id', foreignKeySortFields: ['sorting'] },
    { name: 'maxStatus', text: 'Linjestatus', tooltipText: 'Linjestatus', widthType: FieldWidthEnum.S, foreignKeyTableName: 'WarehouseOutboundOrderStatus', foreignKeyField: 'id', foreignKeySortFields: ['sorting'] },
    { name: 'salesOrder', text: 'Salgsordre', widthType: FieldWidthEnum.XS, visible: false, visibleEdit: true },
    { name: 'releasedDate', text: 'Frigivet', format: 'DateShort', visible: false, visibleEdit: true },
    { name: 'confirmedDate', text: 'Bekræftet', format: 'DateShort', visible: false, visibleEdit: true },
    { name: 'reservedDate', text: 'Reserveret', format: 'DateShort', visible: false, visibleEdit: true },
    { name: 'shipDateRequested', text: 'Ønsket', format: 'DateShort' },
    { name: 'shipDateDispatched', text: 'Afsendt', format: 'DateShort', visible: false, visibleEdit: true },
    { name: 'name', text: 'Navn', tooltipText: 'Navn', widthType: FieldWidthEnum.L },
    { name: 'note', text: 'Notat', tooltipText: 'Notat', format: 'TextArea', widthType: FieldWidthEnum.L },
    { name: 'email', text: 'E-mail', tooltipText: 'Advisering e-mail', format: 'Email' },
    { name: 'phone', text: 'Telefon', tooltipText: 'Advisering telefon', format: 'Phone' },
    { name: 'carrier', text: 'Transportør', foreignKeyTableName: 'WarehouseCarrier', foreignKeyField: 'num', foreignKeyValue: 'name' },
    { name: 'carrierServicePoint', text: 'Pakkeshop', foreignKeyTableName: 'WarehouseCarrierServicePoint', foreignKeyField: 'keyCode', foreignKeyValue: 'keyCode', foreignKeyColumns: ['keyCountryCode', 'keyType', 'key'] },
    { name: 'trackTrace', text: 'Tracking', format: 'Url', urlBase: baseUrlTrackTrace },
    { name: 'shipName', text: 'Lev. navn 1', tooltipText: 'Levering navn 1', widthType: FieldWidthEnum.XL, visible: false, visibleEdit: true },
    { name: 'shipName2', text: 'Lev. navn 2', tooltipText: 'Levering navn 2', widthType: FieldWidthEnum.XL, visible: false, visibleEdit: true },
    { name: 'shipAddress1', text: 'Lev. adresse 1', tooltipText: 'Levering adresse 1', widthType: FieldWidthEnum.XL, visible: false, visibleEdit: true },
    { name: 'shipAddress2', text: 'Lev. adresse 2', tooltipText: 'Levering adresse 2', widthType: FieldWidthEnum.XL, visible: false, visibleEdit: true },
    { name: 'shipPostalCode', text: 'Lev. postnr.', tooltipText: 'Levering postnr.', widthType: FieldWidthEnum.S, visible: false, visibleEdit: true },
    { name: 'shipPostalDistrict', text: 'Lev. by', tooltipText: 'Levering by', widthType: FieldWidthEnum.L, visible: false, visibleEdit: true },
    {
      name: 'shipCountryCode', text: 'Lev. land', tooltipText: 'Levering landekode', widthType: FieldWidthEnum.S, visible: false, visibleEdit: true,
      foreignKeyTableName: 'Country', foreignKeyField: 'num', foreignKeyValue: 'name', foreignKeyColumns: ['num', 'name'], foreignKeyTitles: ['Kode', 'Land'], foreignKeyColumnsWidth: ['100px', '300px']
    },
  ];

  public orderFieldGroupMetadata: FieldGroupMetadata[] = [
    { idx: 1, tabIdx: 0, name: 'identification', text: 'Identifikation', fields: ['id', 'num', 'warehouse', 'salesOrder', 'type', 'status'], width: 240 },
    { idx: 2, tabIdx: 0, name: 'shipTo', text: 'Modtager', fields: ['name', 'email', 'phone'] },
    { idx: 3, tabIdx: 0, name: 'carrier', text: 'Transportør', fields: ['carrier', 'carrierServicePoint', 'note', 'trackTrace'] },
    { idx: 4, tabIdx: 0, name: 'shipment', text: 'Adresse', fields: ['shipName', 'shipName2', 'shipAddress1', 'shipAddress2', 'shipPostalCode', 'shipPostalDistrict', 'shipCountryCode'] },
    { idx: 5, tabIdx: 0, name: 'dates', text: 'Datoer', fields: ['created', 'releaseDate', 'confirmDate', 'reservedDate', 'shipDateRequested', 'shipDateDispatched'] },
    { idx: 6, tabIdx: 0, name: 'other', text: 'Øvrige', fields: [] }
  ];

  // Order line view
  public orderLineViewMetadata: ViewMetadata = {
    name: 'outbound-orderline', dataApiName: 'WarehouseOutboundOrderLine', text: 'Forsendelse linjer', textSingular: 'Forsendelse linje',
    dataApiParams: ['$includeDataLinks', '$statusOnHand'], primaryKey: 'id', parentPrimaryKey: 'id', parentForeignKey: 'order', titleFields: ['order', 'type', 'product'], allowEdit: true, allowCreate: true, allowDelete: true, baseFields: ['history']
  };

  public orderLineFieldMetadata: FieldMetadata[] = [
    { name: 'id', text: 'Id', tooltipText: 'Unik nøgle', allowEdit: false, visible: false, widthType: FieldWidthEnum.XS },
    { name: 'order', text: 'Forsendelse', visible: false },
    { name: 'line', text: 'Linje', format: 'Integer', widthType: FieldWidthEnum.XS, visible: false, orderBy: 'Ascending', orderByPriority: 1 },
    { name: 'product', fieldType: 'productLookup' },
    { name: 'name', text: 'Navn.', tooltipText: 'Ordrenummer', widthType: FieldWidthEnum.XXL },
    { name: 'orderedQty', text: 'Plukantal', format: 'Number', footerType: 'Sum', widthType: FieldWidthEnum.XXS, allowFiltering: false, allowSorting: false },
    { name: 'orderedInventQty', text: 'Vareantal', format: 'Number', footerType: 'Sum', widthType: FieldWidthEnum.XXS, allowFiltering: false, allowSorting: false },
    { name: 'reservedQty', text: 'Reserveret antal', allowEdit: false, format: 'Number', footerType: 'Sum', widthType: FieldWidthEnum.XXS, allowFiltering: false, allowSorting: false },
    { name: 'unreservedQty', text: 'Ej reserveret antal', allowEdit: false, format: 'Number', footerType: 'Sum', widthType: FieldWidthEnum.XXS, visible: false, allowFiltering: false, allowSorting: false },
    { name: 'deliveredQty', text: 'Leveret antal', allowEdit: false, format: 'Number', widthType: FieldWidthEnum.XXS, footerType: 'Sum', allowFiltering: false, allowSorting: false },
    { name: 'status', text: 'Status', tooltipText: 'Forsendelse linjestatus', widthType: FieldWidthEnum.S, foreignKeyTableName: 'WarehouseOutboundOrderStatus', foreignKeyField: 'id', foreignKeySortFields: ['sorting'] },
    { name: 'onHandInventQty', text: 'Lagerbeholdning', format: 'Number', allowEdit: false, widthType: FieldWidthEnum.XXS, onQueryCellInfo: this.gridQueryCellInfoOnHandInventQty },
    { name: 'trackTrace', text: 'Tracking', format: 'Url', urlBase: baseUrlTrackTrace, widthType: FieldWidthEnum.XS },
  ];

  // Data notes view
  public dataNoteViewMetadata: ViewMetadata = null;
  public dataNoteFieldMetadata: FieldMetadata[] = null;


  private warehouseOutboundOrderGrid: ICerViewComponent;
  public setWarehouseOutboundOrderGrid(grid: ICerViewComponent) {
    this.warehouseOutboundOrderGrid = grid;
    this.manage((this.warehouseOutboundOrderGrid as CerGridComponent).commmand.subscribe((event: UiCommandEvent) => this.onCommand(event)));
  }

  public onCommand(event: UiCommandEvent) {
    switch (event.commandId) {
      case uiCmdWarehouseOutboundOrderRelease.id:
        this.outboundOrderSetStatusFromEvent(event, WarehouseOutboundOrderStatusEnum.Released);
        break;
      case uiCmdWarehouseOutboundOrderComplete.id:
        this.outboundOrderSetStatusFromEvent(event, WarehouseOutboundOrderStatusEnum.Delivered);
        break;
      case uiCmdWarehouseOutboundOrderCancel.id:
        this.outboundOrderSetStatusFromEvent(event, WarehouseOutboundOrderStatusEnum.Cancelled);
        break;
      case uiCmdSalesOrderSync.id:
        this.outboundOrdersSync();
        break;
      case uiCmdOrderSyncSelected.id:
        this.outboundOrderSyncFromEvent(event);
        break;
      case uiCmdWarehouseOutboundOrderReorderCancelledLines.id:
        this.outboundOrderReorderCancelledLinesFromEvent(event);
        break;
    }
  }
  private getOrderNumFromEvent(event: UiCommandEvent): string {
    return event?.rowData?.num;
  }

  // Sync
  private outboundOrderSyncFromEvent(event: UiCommandEvent = null) {
    var outboundOrderNum = this.getOrderNumFromEvent(event);
    if (outboundOrderNum != null) {
      this.outboundOrdersSync(outboundOrderNum);
    }
  }

  private async outboundOrdersSync(outboundOrderNum: string = null) {
    this.ui.operationBegin('Opdaterer forsendelser');
    (outboundOrderNum != null ? this.outboundOrderClient.syncOutboundOrder(outboundOrderNum) : this.outboundOrderClient.syncOutboundOrders())
      .subscribe({
        next: result => {
          this.ui.operationEnd();
          this.warehouseOutboundOrderGrid.research();
        },
        error: async (error) => {
          this.ui.operationEndError(error);
        }
      });
  }

  private outboundOrderSetStatusFromEvent(event: UiCommandEvent, status: WarehouseOutboundOrderStatusEnum) {
    var orderNum: string = this.getOrderNumFromEvent(event);
    if (orderNum) {
      this.outboundOrderSetStatus(orderNum, status);
    }
  }

  private outboundOrderSetStatus(orderNum: string, status: WarehouseOutboundOrderStatusEnum) {
    if (orderNum) {
      this.ui.operationBegin('Opdatering forsendelse fremdrift');
      this.outboundOrderClient.setStatus(orderNum, status).subscribe({
        next: (result) => {
          this.ui.operationEnd('Opdatering forsendelse fremdrift');
          if (result) {
            this.warehouseOutboundOrderGrid.research();
          }
        },
        error: async (error) => {
          this.ui.errorToast('Opdatering forsendelse fremdrift');
          this.ui.operationEndError(error);
        }
      });
    }
  }

  // Reorder cancelled lines
  private outboundOrderReorderCancelledLinesFromEvent(event: UiCommandEvent) {
    var outboundOrderNum = this.getOrderNumFromEvent(event);
    if (outboundOrderNum != null) {
      this.outboundOrderReorderCancelledLines(outboundOrderNum);
    }
  }

  private outboundOrderReorderCancelledLines(orderNum: string) {
    if (orderNum) {
      this.ui.operationBegin('Genopretter forsendelse annullerede linjer');
      this.outboundOrderClient.createOrderFromCancelledLines(orderNum).subscribe({
        next: (result) => {
          this.ui.operationEnd('Genopret forsendelse annullerede linjer');
          if (result) {
            this.warehouseOutboundOrderGrid.research();
          }
        },
        error: async (error) => {
          this.ui.operationEndError(error);
          this.ui.errorToast('Genopret forsendelse annullerede linjer');
        }
      });
    }
  }

  public gridQueryCellInfoOnHandInventQty(args: any) {
    var dto: any = args.data;

    if ((dto != null) && (dto.orderedQty > 0) && (dto.onHandInventQty < dto.orderedQty)) {
      args.cell.style.backgroundColor = '#FFBBBB';
    }
  }
}